/* eslint-disable import/no-unresolved */
import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  Receipt as ReceiptIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  AccountBalanceWalletOutlined as AccountBalanceWalletOutlinedIcon,
  RemoveCircle as RemoveCircleIcon
} from "@mui/icons-material";

// eslint-disable-next-line import/no-unresolved
import ArcChart from "../../../../components/Dashboard/ArcChart";
import BarCharts from "../../../../components/Dashboard/BarChart";
import StatBox from "../../../../components/Dashboard/StatBox";
import TopPerformance from "../../../../components/Dashboard/TopPerformance";
import color from "../../../../constants/color";
import styles from "./style";

const HomePage = ({ payrollStats, allStats, attendanceOnly = false }) => {
  const theme = useTheme();

  const payroll = payrollStats || {};
  const all = allStats || {};

  const numbers = Array.isArray(all.upcomingHolidays)
    ? all.upcomingHolidays.map((h) => new Date(h.start).getDate())
    : [2, 6, 11, 20];

  const timesheetStats = [
    { label: "Submitted", value: all?.timesheets?.created ?? 0 },
    { label: "Approved", value: all?.timesheets?.approved ?? 0 },
    { label: "Rejected", value: all?.timesheets?.rejected ?? 0 },
    { label: "Pending", value: all?.timesheets?.pending ?? 0 },
  ];
  
  const claimsStats = [
    { label: "Submitted", value: all?.travelAllowance?.created ?? 0 },
    { label: "Approved", value: all?.travelAllowance?.approved ?? 0 },
    { label: "Rejected", value: all?.travelAllowance?.rejected ?? 0 },
    { label: "Pending", value: all?.travelAllowance?.pending ?? 0 },
  ];

  const leaveStats = [
    { label: "Submitted", value: all?.leaves?.created ?? 0 },
    { label: "Approved", value: all?.leaves?.approved ?? 0 },
    { label: "Rejected", value: all?.leaves?.rejected ?? 0 },
    { label: "Pending", value: all?.leaves?.pending ?? 0 },
  ];

  const monthlyCounts = Array.isArray(payroll?.monthlyPayslips)
    ? payroll.monthlyPayslips.map((m) => m.count)
    : null;
  const monthlyLabels = Array.isArray(payroll?.monthlyPayslips)
    ? payroll.monthlyPayslips.map((m) => m.month)
    : null;
  const maxBarParsed = Number(payroll?.maxBar);
  const maxBarValue = Number.isFinite(maxBarParsed) ? maxBarParsed : null;

  const renderAttendanceCard = (boxProps = {}) => (
    <Box
      gridColumn={{ xs: "span 12", md: "span 6" }}
      gridRow="span 2"
      backgroundColor={theme.palette.secondary.main}
      borderRadius="20px"
      p="20px"
      {...boxProps}
    >
      <Typography sx={{ ...styles.cardTitle, color: color.card }}>
        Attendance Today
      </Typography>
      <Box display="flex" flexDirection="row" alignItems="center" mt="15px">
        <ArcChart value={payroll?.attendancePercentage ?? 0} text={"Attendance"} />
        <Box display="flex" flexDirection="column" ml="20px" width="50%">
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", mb: "5px " }}>
            <Typography sx={{ color: "#fff" }}>{"Total Employees"}</Typography>
            <Typography sx={{ color: "#fff" }}>{payroll?.totalEmployees ?? 0}</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", mb: "5px " }}>
            <Typography sx={{ color: "#fff" }}>{"Present Today "}</Typography>
            <Typography sx={{ color: "#fff" }}>{payroll?.presentToday ?? 0}</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", mb: "5px " }}>
            <Typography sx={{ color: "#fff" }}>{"Absent Today"}</Typography>
            <Typography sx={{ color: "#fff" }}>{payroll?.absentToday ?? 0}</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", mb: "5px " }}>
            <Typography sx={{ color: "#fff" }}>{"Not Reported"}</Typography>
            <Typography sx={{ color: "#fff" }}>{payroll?.notReported ?? 0}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  if (attendanceOnly) {
    return (
      <Box p="20px" backgroundColor={theme.palette.lightGray.main}>
        {renderAttendanceCard({ maxWidth: "480px", width: "100%", mx: "auto" })}
      </Box>
    );
  }

  return (
    <>
      <Box p="20px" backgroundColor={theme.palette.lightGray.main} >
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gridAutoRows="auto"
          gap="20px"
        >
          {/* ROW 1 */}
          <Box
            gridColumn={{ xs: "span 12", md: "span 3" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            backgroundColor={theme.palette.white.main}
            borderRadius='20px'
            p="10px"
          >
            <StatBox
              title={payroll?.totalPayslips ?? 0}
              subtitle="Total Payslips"
              progress="0.75"
              increase="+0%"
              icon={<ReceiptIcon sx={{ color: theme.palette.primary.main, fontSize: "20px" }} />}
            />
          </Box>
          <Box
            gridColumn={{ xs: "span 12", md: "span 3" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            backgroundColor={theme.palette.white.main}
            borderRadius='20px'
            p="10px"
          >
            <StatBox
              title={payroll?.issuedPayslips ?? 0}
              subtitle="Issued Payslips"
              progress="0.50"
              increase="+0%"
              icon={<AssignmentTurnedInIcon sx={{ color: theme.palette.primary.main, fontSize: "20px" }} />}
            />
          </Box>
          {renderAttendanceCard()}
          <Box
            gridColumn={{ xs: "span 12", md: "span 3" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            backgroundColor={theme.palette.white.main}
            borderRadius='20px'
            p="10px"
          >
            <StatBox
              title={payroll?.totalLoans ?? 0}
              subtitle="Total Loans"
              progress="0.30"
              increase="+0%"
              icon={<AccountBalanceWalletOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: "20px" }} />}
            />
          </Box>
          <Box
            gridColumn={{ xs: "span 12", md: "span 3" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            backgroundColor={theme.palette.white.main}
            borderRadius='20px'
            p="10px"
          >
            <StatBox
              title={payroll?.totalDeductions ?? 0}
              subtitle="Total Deductions"
              progress="0.80"
              decrease="-0%"
              icon={<RemoveCircleIcon sx={{ color: theme.palette.primary.main, fontSize: "20px" }} />}
            />
          </Box>

          {/* ROW 2 */}
          <Box
            gridColumn={{ xs: "span 12", md: "span 6" }}
            gridRow="span 2"
            backgroundColor={theme.palette.white.main}
            borderRadius="20px"
            p="20px 20px 0px 20px"
          >
            <Typography
              sx={styles.cardTitle}
            >
              Payroll Stats
            </Typography>
            <Box height="100%" style={{ width: "100%" }}>
              <BarCharts seriesData={monthlyCounts} labels={monthlyLabels} maxValue={maxBarValue} />
            </Box>
          </Box>
          <Box
            gridColumn={{ xs: "span 12", md: "span 6" }}
            gridRow="span 2"
            backgroundColor={theme.palette.white.main}
            borderRadius="20px"
            p="10px"
          >
            <Box sx={styles.boxNotification}>
              <Typography sx={styles.cardTitle}>
                Timesheets
              </Typography>
            </Box>
            <Box sx={styles.timesheetGrid}>
              {timesheetStats.map((item, idx) => (
                <Box key={idx} sx={{...styles.timesheetCard,}}>
                  <Typography sx={styles.timesheetLabel}>{item.label}</Typography>
                  <Typography sx={styles.timesheetValue}>{item.value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            gridColumn={{ xs: "span 12", md: "span 6" }}
            gridRow="span 2"
            backgroundColor={theme.palette.white.main}
            borderRadius="20px"
            p="10px"
          >
            <Box sx={styles.boxNotification}>
              <Typography sx={styles.cardTitle}>
                Travel Claims
              </Typography>
            </Box>
            <Box sx={styles.timesheetGrid}>
              {
              claimsStats.map((item, idx) => (
                <Box key={idx} sx={styles.timesheetCard}>
                  <Typography sx={styles.timesheetLabel}>{item.label}</Typography>
                  <Typography sx={styles.timesheetValue}>{item.value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            gridColumn={{ xs: "span 12", md: "span 6" }}
            gridRow="span 2"
            backgroundColor={theme.palette.white.main}
            borderRadius="20px"
            p="10px"
          >
            <Box sx={styles.boxNotification}>
              <Typography sx={styles.cardTitle}>
                Leaves
              </Typography>
            </Box>
            <Box sx={styles.timesheetGrid}>
              {
              leaveStats.map((item, idx) => (
                <Box key={idx} sx={styles.timesheetCard}>
                  <Typography sx={styles.timesheetLabel}>{item.label}</Typography>
                  <Typography sx={styles.timesheetValue}>{item.value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            gridColumn="span 12"
            gridRow="span 1"
            backgroundColor={theme.palette.white.main}
            borderRadius='20px'
            p="20px"
          >
            <Box my="15px">
              <TopPerformance data={all?.topPerformers} />
            </Box>
          </Box>
          <Box
            gridColumn={{ xs: "span 12", md: "span 12" }}
            gridRow="span 2"
            backgroundColor={color.card}
            borderRadius="20px"
            p="20px"
          >
            <Typography sx={styles.cardTitle}>
              Upcoming Holidays
            </Typography>
            <Typography sx={styles.viewAll}>
              (This Month)
            </Typography>
            <Box sx={{ display: "flex", mt: 2 , width : '100%'}}>
              {numbers.map((number, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="50px"
                  height="50px"
                  borderRadius="50%"
                  bgcolor={color.primary}
                  color={color.card}
                  marginRight="12px"
                >
                  <Typography variant="h6">{number}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default HomePage;
